/**
 * Minimal runtime transformation utilities to restore real class instances
 * (methods/getters intact) from plain JSON, without decorators or reflect-metadata.
 *
 * Why this exists:
 * - We removed class-transformer/reflect-metadata to cut bundle size and complexity.
 * - Explicit, opt-in schemas avoid surprising coercions and timezone pitfalls.
 * - We preserve class methods by assigning onto actual instances (not plain objects).
 */
export type Constructor<T> = new (...args: any[]) => T;

/**
 * Schema specification for a field mapping:
 * - 'date'       → coerce values into Date (explicit opt-in; avoids accidental parsing)
 * - Ctor         → nested object restored via plainToClass(Ctor, value)
 * - [Ctor]       → array of nested objects restored item-wise
 *
 * Why explicit schemas: relying on runtime type metadata adds weight and is brittle
 * across bundlers. This keeps behavior predictable and tree-shakable.
 */
type FieldSpec = 'date' | Constructor<any> | [Constructor<any>];
type TransformSchema<T = any> = Partial<Record<keyof T & string, FieldSpec>>;

/**
 * Holds per-class schemas. WeakMap prevents memory leaks for short-lived classes
 * (e.g., in tests) and allows GC when constructors fall out of scope.
 */
const schemaRegistry: WeakMap<Function, TransformSchema<any>> = new WeakMap();

/**
 * Registers a transform schema for a class.
 *
 * @param ctor Class constructor to associate the schema with
 * @param schema Field mapping describing how to restore nested objects/arrays/dates
 */
export function registerSchema<T>(ctor: Constructor<T>, schema: TransformSchema<T>): void {
  schemaRegistry.set(ctor, schema as TransformSchema<any>);
}

function isObject(value: unknown): value is Record<string, any> {
  return typeof value === 'object' && value !== null;
}

/**
 * Date coercion used when a schema field is marked as 'date'.
 *
 * Why opt-in: JSON transports have no native Date type; implicit parsing often
 * introduces timezone ambiguity. Requiring 'date' in the schema makes conversions explicit.
 */
function coerceDate(value: any): Date | null {
  if (value == null) return value;
  if (value instanceof Date) return value;
  if (typeof value === 'number' || typeof value === 'string') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? (null as any) : d;
  }
  return (null as any);
}

/**
 * Restores a plain value into a real instance of the provided class.
 * - Uses the registered schema (if any) to recursively restore nested objects/arrays.
 * - Unknown fields are shallow-assigned as-is.
 * - If a value is already an instance, it is returned as-is.
 *
 * Why instantiate: assigning onto `new Ctor()` preserves methods and default
 * initializers. If a constructor requires params, we fall back to a prototype-only
 * instance to avoid side effects while still restoring methods/getters.
 *
 * @param ctor Class constructor to instantiate
 * @param plain Plain object (e.g., parsed JSON) to restore
 */
export function plainToClass<T>(ctor: Constructor<T>, plain: any): T {
  if (!isObject(plain)) {
    // If not an object, instantiate and return defaults
    return instantiate<T>(ctor);
  }

  if (plain instanceof ctor) {
    return plain;
  }

  const schema = schemaRegistry.get(ctor) as TransformSchema<T> | undefined;
  const instance = instantiate<T>(ctor);

  // Assign properties from plain object
  for (const key of Object.keys(plain)) {
    const value = (plain as any)[key];
    const spec = schema?.[key as keyof T & string] as FieldSpec | undefined;

    if (!spec) {
      (instance as any)[key] = value;
      continue;
    }

    if (spec === 'date') {
      (instance as any)[key] = coerceDate(value);
      continue;
    }

    if (Array.isArray(spec)) {
      const itemCtor = spec[0];
      if (value == null) {
        (instance as any)[key] = value;
      } else if (Array.isArray(value)) {
        (instance as any)[key] = value.map((v) => plainToClass(itemCtor as any, v));
      } else {
        (instance as any)[key] = [];
      }
      continue;
    }

    // Nested object
    if (value == null) {
      (instance as any)[key] = value;
    } else {
      (instance as any)[key] = plainToClass(spec as Constructor<any>, value);
    }
  }

  return instance;
}

/**
 * Helper to restore arrays of plain values into arrays of class instances.
 *
 * @param ctor Class constructor for items
 * @param arr Plain array to restore
 */
export function plainArrayToClassArray<T>(ctor: Constructor<T>, arr: any): T[] {
  if (!Array.isArray(arr)) return [];
  return arr.map((item) => plainToClass(ctor, item));
}

/**
 * Creates an instance while balancing correctness and safety:
 * - First tries `new Ctor()` to apply field default initializers defined in class bodies.
 * - If the constructor requires parameters or has side effects, we catch and
 *   fall back to `Object.create(Ctor.prototype)` to preserve the prototype chain
 *   and methods without running the constructor.
 */
function instantiate<T>(ctor: Constructor<T>): T {
  try {
    // Prefer calling the constructor to get default field initializers
    return new (ctor as any)();
  } catch {
    // Fallback to prototype-only instance if constructor requires params
    return Object.create(ctor.prototype);
  }
}


